import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import PauseCircleOutlinedIcon from "@mui/icons-material/PauseCircleOutlined";
import CustomTooltip from "components/CustomTooltip/CustomTooltip";

import { CARD_BG, CARD_BG_HOVER, CARD_TEXT, CARD_TEXT_SUBDUED, METRIC_BORDER, BADGE_STYLES } from "./cardTokens";

const BADGE_ICONS = {
  atRisk: WarningAmberOutlinedIcon,
  onHold: PauseCircleOutlinedIcon,
};

// onClick is optional - when provided the badge becomes its own clickable target (with
// its own hover-inverted color, per Figma's At risk/hover + On hold/hover variants) and
// stops the click from also reaching the card's own onClick, so clicking the badge adds
// the atRisk/onHold filter instead of just doing the card's normal status/type filter.
//
// compact drops the "At risk"/"On hold" word and shows just ":count" - used when a card
// has to show both badges at once (see MetricCard) so the pair reads as a small legend
// under the value instead of crowding the value's own row.
const MetricBadge = ({ type, count, onClick, compact = false, tooltip }) => {
  const [isHovered, setIsHovered] = useState(false);
  if (!count || count <= 0) return null;
  const { background, color, label, hoverBackground, hoverColor } = BADGE_STYLES[type];
  const Icon = BADGE_ICONS[type];
  const clickable = Boolean(onClick);
  const activeBackground = clickable && isHovered ? hoverBackground : background;
  const activeColor = clickable && isHovered ? hoverColor : color;

  const badge = (
    <Box
      onClick={
        clickable
          ? (event) => {
              event.stopPropagation();
              onClick(type);
            }
          : undefined
      }
      onMouseEnter={clickable ? () => setIsHovered(true) : undefined}
      onMouseLeave={clickable ? () => setIsHovered(false) : undefined}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        height: "28px",
        px: "8px",
        borderRadius: "6px",
        backgroundColor: activeBackground,
        minWidth: 0,
        maxWidth: "100%",
        overflow: "hidden",
        cursor: clickable ? "pointer" : "default",
        transition: "background-color 0.15s ease, color 0.15s ease",
      }}
    >
      <Icon sx={{ fontSize: 16, color: activeColor, flexShrink: 0 }} />
      <Typography
        sx={{
          margin: 0,
          fontSize: "14px",
          fontWeight: 400,
          lineHeight: "20px",
          letterSpacing: "-0.04px",
          color: activeColor,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {compact ? `: ${count}` : `${label}: ${count}`}
      </Typography>
    </Box>
  );

  return tooltip ? (
    <CustomTooltip title={tooltip} arrow placement="top">
      {badge}
    </CustomTooltip>
  ) : (
    badge
  );
};

// Layout matches Figma's Metric component: label on top, then a row with the value on
// the left and (when present) status badge(s) right-aligned on the same row, both
// vertically centered - as opposed to the value being the large/featured element with
// the label underneath.
//
// When a card carries *both* an at-risk and an on-hold count at once, there's no room
// for two full "At risk: N"/"On hold: N" badges next to the value, so both switch to
// the compact ":count" form - but they stay right-aligned on the value's own row.
const MetricCard = ({ value, label, atRiskCount, onHoldCount, onClick, onBadgeClick, sectionLabel }) => {
  const hasAtRisk = atRiskCount > 0;
  const hasOnHold = onHoldCount > 0;
  const hasBoth = hasAtRisk && hasOnHold;
  const hasBadge = hasAtRisk || hasOnHold;

  return (
    <Box
      onClick={onClick}
      sx={{
        flex: "1 0 0",
        minWidth: 0,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        px: "20px",
        pt: "16px",
        pb: "12px",
        borderRadius: "12px",
        border: `1px solid ${METRIC_BORDER}`,
        backgroundColor: CARD_BG,
        overflow: "hidden",
        cursor: onClick ? "pointer" : "default",
        transition: "background-color 0.15s ease",
        "&:hover": onClick ? { backgroundColor: CARD_BG_HOVER } : undefined,
      }}
    >
      <Typography
        sx={{
          margin: 0,
          fontSize: "16px",
          fontWeight: 400,
          lineHeight: "24px",
          letterSpacing: "-0.04px",
          color: CARD_TEXT_SUBDUED,
          width: "100%",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: hasBadge ? "space-between" : "flex-start",
          width: "100%",
          minWidth: 0,
          gap: "8px",
        }}
      >
        <Typography
          sx={{
            margin: 0,
            fontSize: "20px",
            fontWeight: 400,
            lineHeight: "32px",
            letterSpacing: "-0.06px",
            color: CARD_TEXT,
            flexShrink: 0,
          }}
        >
          {value ?? 0}
        </Typography>
        {hasBadge && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0, overflow: "hidden" }}>
            <MetricBadge
              type="atRisk"
              count={atRiskCount}
              compact={hasBoth}
              tooltip={sectionLabel ? `Click to view at risk ${sectionLabel}.` : undefined}
              onClick={onBadgeClick ? () => onBadgeClick("atRisk") : undefined}
            />
            <MetricBadge
              type="onHold"
              count={onHoldCount}
              compact={hasBoth}
              tooltip={sectionLabel ? `Click to view on hold ${sectionLabel}.` : undefined}
              onClick={onBadgeClick ? () => onBadgeClick("onHold") : undefined}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MetricCard;
export { MetricBadge };
